import { css, type SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, styleguideSpacing as SPACING } from '@kaiizen/kai-shiki/core';

export type FocusedProjectOverviewPanelStyles = {
  root: SerializedStyles;
  summaryModule: SerializedStyles;
  hero: SerializedStyles;
  heroCaption: SerializedStyles;
  summaryDetails: SerializedStyles;
  projectHeader: SerializedStyles;
  projectName: SerializedStyles;
  projectAddress: SerializedStyles;
  budget: SerializedStyles;
  budgetAmount: SerializedStyles;
  budgetLabel: SerializedStyles;
  statGrid: SerializedStyles;
  statsRest: SerializedStyles;
  stat: SerializedStyles;
  statButton: SerializedStyles;
  statLabel: SerializedStyles;
  statValue: SerializedStyles;
  detailsBand: SerializedStyles;
  bandTitle: SerializedStyles;
  bandActions: SerializedStyles;
  bandAction: SerializedStyles;
  detailGrid: SerializedStyles;
  detail: SerializedStyles;
  detailWide: SerializedStyles;
  detailLabel: SerializedStyles;
  detailValue: SerializedStyles;
  marketButton: SerializedStyles;
  templateButton: SerializedStyles;
  detailInput: SerializedStyles;
  detailSelect: SerializedStyles;
  accessButton: SerializedStyles;
  accessDetails: SerializedStyles;
  contractorModule: SerializedStyles;
  moduleHeader: SerializedStyles;
  moduleTitle: SerializedStyles;
  moduleActions: SerializedStyles;
  moduleAction: SerializedStyles;
  moduleToggle: SerializedStyles;
  moduleEmpty: SerializedStyles;
  contractorBody: SerializedStyles;
  contractorGeneral: SerializedStyles;
  contractorSummaryLabel: SerializedStyles;
  contractorSummaryValue: SerializedStyles;
  contractorList: SerializedStyles;
  contractorOverrideList: SerializedStyles;
  contractorRow: SerializedStyles;
  contractorRowContent: SerializedStyles;
  contractorProvenance: SerializedStyles;
  contractorName: SerializedStyles;
  contractorTaskCount: SerializedStyles;
  contractorUnassigned: SerializedStyles;
  contractorEditor: SerializedStyles;
  activityModule: SerializedStyles;
  activityTable: SerializedStyles;
  activityTableHeader: SerializedStyles;
  activityAmountsHeader: SerializedStyles;
  activityPeriod: SerializedStyles;
  activityPeriodAlternate: SerializedStyles;
  activityRow: SerializedStyles;
  activityAmounts: SerializedStyles;
  activityMarker: SerializedStyles;
  activityMarkerDot: SerializedStyles;
  activityWhat: SerializedStyles;
  activityWhatButton: SerializedStyles;
  activityCaret: SerializedStyles;
  activityRecipient: SerializedStyles;
  activityArtifact: SerializedStyles;
  activityArtifactLabel: SerializedStyles;
  activityArtifactButton: SerializedStyles;
  activityArtifactStatus: SerializedStyles;
  activityNote: SerializedStyles;
  activityNoteButton: SerializedStyles;
  activityAmount: SerializedStyles;
  activityIncrease: SerializedStyles;
  activityDecrease: SerializedStyles;
  activityDetails: SerializedStyles;
  activityDetailGroup: SerializedStyles;
  activityDetailGroupName: SerializedStyles;
  activityDetailTaskList: SerializedStyles;
  activityTaskChange: SerializedStyles;
  activityTaskId: SerializedStyles;
  activityTaskSummary: SerializedStyles;
  activityTaskName: SerializedStyles;
  activityTaskDescription: SerializedStyles;
  activityMore: SerializedStyles;
  fullWidthSection: SerializedStyles;
};

export const DEFAULT_FOCUSED_PROJECT_OVERVIEW_PANEL_STYLES: FocusedProjectOverviewPanelStyles = {
  root: css`
    label: FOCUSED_PROJECT_OVERVIEW_PANEL;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${SPACING.s3}px;
    min-height: 100%;
    overflow-y: auto;
    padding: calc(${SPACING.s4}px + ${SPACING.s1}px / 2) calc(${SPACING.s5}px - ${SPACING.s1}px / 2) calc(${SPACING.s5}px + ${SPACING.s4}px);
    background: ${COLORS.shell};
    container: focused-project-overview-root / inline-size;
  `,
  summaryModule: css`
    label: FOCUSED_PROJECT_OVERVIEW_SUMMARY_MODULE;
    display: grid;
    flex-grow: 0;
    grid-template-columns: calc(${SPACING.s8}px * 3 + ${SPACING.s1}px) minmax(0, 1fr);
    column-gap: calc(${SPACING.s4}px + ${SPACING.s1}px / 2);
    align-items: stretch;
    min-height: max-content;
    overflow: visible;
    padding: calc(${SPACING.s3}px + ${SPACING.s1}px / 2) calc(${SPACING.s4}px + ${SPACING.s1}px / 2);
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.paper};

    @container focused-project-overview-root (max-width: 720px) {
      grid-template-columns: 1fr;
    }

    @container focused-project-overview-root (min-width: 960px) {
      grid-template-columns: calc(${SPACING.s8}px + ${SPACING.s6}px) repeat(5, minmax(0, 1fr)) auto;
      column-gap: ${SPACING.s5}px;
    }
  `,
  hero: css`
    label: FOCUSED_PROJECT_OVERVIEW_HERO;
    position: relative;
    align-self: stretch;
    min-height: calc(${SPACING.s8}px + ${SPACING.s5}px + ${SPACING.s4}px);
    overflow: hidden;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.sunken};

    & > div:first-of-type {
      position: absolute;
      inset: 0;
    }

    & > div:first-of-type > label {
      height: 100%;
      aspect-ratio: auto;
    }

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 1 / 3;
    }

    @container focused-project-overview-root (max-width: 720px) {
      max-width: calc(${SPACING.s8}px * 4 + ${SPACING.s1}px);
      aspect-ratio: 4 / 3;
      height: auto;
      margin-bottom: ${SPACING.s4}px;
    }
  `,
  heroCaption: css`
    label: FOCUSED_PROJECT_OVERVIEW_HERO_CAPTION;
    position: absolute;
    z-index: 1;
    bottom: 0;
    left: 0;
    padding: ${SPACING.s1}px ${SPACING.s2}px;
    background: ${COLORS.ink};
    color: ${COLORS.paper};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
  summaryDetails: css`
    label: FOCUSED_PROJECT_OVERVIEW_SUMMARY_DETAILS;
    display: flex;
    flex-direction: column;
    min-width: 0;

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 3 / -1;
      display: grid;
      grid-template-columns: subgrid;
      align-content: start;
    }
  `,
  projectHeader: css`
    label: FOCUSED_PROJECT_OVERVIEW_PROJECT_HEADER;
    display: flex;
    align-items: flex-start;
    gap: calc(${SPACING.s3}px + ${SPACING.s1}px / 2);
    justify-content: space-between;
    margin-bottom: 11px;

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 1 / -1;
    }
  `,
  projectName: css`
    label: FOCUSED_PROJECT_OVERVIEW_PROJECT_NAME;
    margin: 0;
    color: ${COLORS.ink};
    font-family: ${FONT.display};
    font-size: ${pxToRem(FONT_SIZE.displayModalTitle)};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: 1.1;
  `,
  projectAddress: css`
    label: FOCUSED_PROJECT_OVERVIEW_PROJECT_ADDRESS;
    color: ${COLORS.textSecondary};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    line-height: 1.4;
  `,
  budget: css`
    label: FOCUSED_PROJECT_OVERVIEW_BUDGET;
    flex-shrink: 0;
    text-align: right;
  `,
  budgetAmount: css`
    label: FOCUSED_PROJECT_OVERVIEW_BUDGET_AMOUNT;
    color: ${COLORS.ink};
    font-family: ${FONT.display};
    font-size: ${pxToRem(FONT_SIZE.displayMedium + SPACING.s1 / 2)};
    font-weight: ${FONT_WEIGHT.bold};
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  `,
  budgetLabel: css`
    label: FOCUSED_PROJECT_OVERVIEW_BUDGET_LABEL;
    margin-bottom: ${SPACING.s1}px;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
  statGrid: css`
    label: FOCUSED_PROJECT_OVERVIEW_STAT_GRID;
    display: block;
    padding: calc(${SPACING.s2}px + ${SPACING.s1}px / 2) 0 11px;
    border-top: 1px solid ${COLORS.stroke};

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: subgrid;
      gap: 0 ${SPACING.s5}px;
    }
  `,
  statsRest: css`
    label: FOCUSED_PROJECT_OVERVIEW_STATS_REST;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    gap: calc(${SPACING.s2}px + ${SPACING.s1}px / 2) ${SPACING.s5}px;

    @container focused-project-overview-root (max-width: 720px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 1 / 4;
      grid-template-columns: subgrid;
    }
  `,
  stat: css`
    label: FOCUSED_PROJECT_OVERVIEW_STAT;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: calc(${SPACING.s1}px / 2);
    min-width: 0;
  `,
  statButton: css`
    label: FOCUSED_PROJECT_OVERVIEW_STAT_BUTTON;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: calc(${SPACING.s1}px / 2);
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
    text-align: left;

    &:hover > :first-of-type {
      text-decoration-color: ${COLORS.ink};
    }
  `,
  statLabel: css`
    label: FOCUSED_PROJECT_OVERVIEW_STAT_LABEL;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
  `,
  statValue: css`
    label: FOCUSED_PROJECT_OVERVIEW_STAT_VALUE;
    overflow: hidden;
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    font-weight: ${FONT_WEIGHT.medium};
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  detailsBand: css`
    label: FOCUSED_PROJECT_OVERVIEW_DETAILS_BAND;
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: calc(${SPACING.s4}px + ${SPACING.s1}px / 2);
    min-height: max-content;
    margin-top: 11px;
    padding-top: 11px;
    border-top: 1px solid ${COLORS.stroke};
    background: ${COLORS.paper};
    overflow: visible;

    @container focused-project-overview-root (max-width: 959px) {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: ${SPACING.s3}px;
    }

    @container focused-project-overview-root (min-width: 960px) {
      display: grid;
      grid-template-columns: subgrid;
      gap: 0 ${SPACING.s5}px;
    }
  `,
  bandTitle: css`
    label: FOCUSED_PROJECT_OVERVIEW_BAND_TITLE;
    flex-shrink: 0;
    width: calc(${SPACING.s8}px + ${SPACING.s6}px);
    margin: 0;
    padding-top: calc(${SPACING.s1}px / 2);
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    line-height: 1.2;
    text-transform: uppercase;

    @container focused-project-overview-root (max-width: 959px) {
      grid-column: 1;
      grid-row: 1;
      width: auto;
    }

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 1;
      width: auto;
    }
  `,
  bandActions: css`
    label: FOCUSED_PROJECT_OVERVIEW_BAND_ACTIONS;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${SPACING.s2}px;

    @container focused-project-overview-root (max-width: 959px) {
      grid-column: 2;
      grid-row: 1;
    }

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 7;
    }
  `,
  bandAction: css`
    label: FOCUSED_PROJECT_OVERVIEW_BAND_ACTION;
    align-self: start;
    padding: ${SPACING.s1}px ${SPACING.s2}px;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.paper};
    color: ${COLORS.ink};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;

    &:hover {
      background: ${COLORS.sunken};
    }

    @container focused-project-overview-root (max-width: 959px) {
      grid-column: 2;
      grid-row: 1;
      justify-self: end;
    }

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 7;
      justify-self: end;
    }
  `,
  detailGrid: css`
    label: FOCUSED_PROJECT_OVERVIEW_DETAIL_GRID;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    flex: 1;
    min-width: 300px;
    gap: 11px ${SPACING.s5}px;
    margin: 0;

    @container focused-project-overview-root (max-width: 959px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-column: 1 / -1;
      grid-row: 2;
      min-width: 0;
    }

    @container focused-project-overview-root (max-width: 520px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 2 / 7;
      grid-template-columns: subgrid;
      min-width: 0;
    }
  `,
  detail: css`
    label: FOCUSED_PROJECT_OVERVIEW_DETAIL;
    min-width: 0;
  `,
  detailWide: css`
    label: FOCUSED_PROJECT_OVERVIEW_DETAIL_WIDE;
    grid-column: span 3;

    @container focused-project-overview-root (max-width: 520px) {
      grid-column: 1 / -1;
    }
  `,
  detailLabel: css`
    label: FOCUSED_PROJECT_OVERVIEW_DETAIL_LABEL;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
  `,
  detailValue: css`
    label: FOCUSED_PROJECT_OVERVIEW_DETAIL_VALUE;
    margin: ${SPACING.s1}px 0 0;
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    font-weight: ${FONT_WEIGHT.medium};
    line-height: 1.3;
    overflow-wrap: anywhere;
  `,
  marketButton: css`
    label: FOCUSED_PROJECT_OVERVIEW_MARKET_BUTTON;
    padding: 0;
    border: 0;
    border-bottom: 1px solid ${COLORS.stroke};
    background: transparent;
    color: ${COLORS.ink};
    cursor: pointer;
    font: inherit;
    text-align: left;

    &:hover {
      border-bottom-color: ${COLORS.ink};
    }
  `,
  templateButton: css`
    label: FOCUSED_PROJECT_OVERVIEW_TEMPLATE_BUTTON;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${COLORS.ink};
    cursor: pointer;
    font: inherit;
    text-align: left;
    text-decoration: underline;
    text-decoration-color: ${COLORS.stroke};
    text-underline-offset: ${SPACING.s1}px;

    &:hover {
      text-decoration-color: ${COLORS.ink};
    }
  `,
  detailInput: css`
    label: FOCUSED_PROJECT_OVERVIEW_DETAIL_INPUT;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: ${SPACING.s5}px;
    margin: 0;
    padding: ${SPACING.s1}px;
    border: 1px solid ${COLORS.stroke};
    border-radius: 0;
    background: ${COLORS.paper};
    color: ${COLORS.ink};
    font: inherit;

    &:focus {
      border-color: ${COLORS.ink};
      outline: 1px solid ${COLORS.ink};
      outline-offset: 1px;
    }
  `,
  detailSelect: css`
    label: FOCUSED_PROJECT_OVERVIEW_DETAIL_SELECT;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: ${SPACING.s5}px;
    margin: 0;
    padding: ${SPACING.s1}px;
    border: 1px solid ${COLORS.stroke};
    border-radius: 0;
    background: ${COLORS.paper};
    color: ${COLORS.ink};
    font: inherit;

    &[multiple] {
      min-height: calc(${SPACING.s7}px + ${SPACING.s2}px);
    }

    &:focus {
      border-color: ${COLORS.ink};
      outline: 1px solid ${COLORS.ink};
      outline-offset: 1px;
    }
  `,
  accessButton: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACCESS_BUTTON;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${COLORS.ink};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.06em;
    text-align: left;
    text-decoration: underline;
    text-underline-offset: ${SPACING.s1}px;
    text-transform: uppercase;

    &:hover {
      color: ${COLORS.textSecondary};
    }
  `,
  accessDetails: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACCESS_DETAILS;
    display: flex;
    flex-wrap: wrap;
    flex-basis: 100%;
    gap: ${SPACING.s2}px ${SPACING.s4}px;
    margin-left: calc(${SPACING.s8}px + ${SPACING.s6}px + ${SPACING.s4}px + ${SPACING.s1}px / 2);
    padding-top: ${SPACING.s2}px;
    border-top: 1px dashed ${COLORS.stroke};
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyDefault)};

    & > span {
      display: flex;
      gap: ${SPACING.s1}px;
    }

    & strong {
      color: ${COLORS.textTertiary};
      font-family: ${FONT.mono};
      font-size: ${pxToRem(FONT_SIZE.monoCaption)};
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    @container focused-project-overview-root (max-width: 959px) {
      grid-column: 1 / -1;
      margin-left: 0;
    }

    @container focused-project-overview-root (min-width: 960px) {
      grid-column: 2 / 7;
      margin: 11px 0 0;
    }
  `,
  contractorModule: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_MODULE;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.paper};
  `,
  moduleHeader: css`
    label: FOCUSED_PROJECT_OVERVIEW_MODULE_HEADER;
    display: flex;
    align-items: center;
    gap: ${SPACING.s3}px;
    justify-content: space-between;
    min-height: ${SPACING.s7}px;
    padding: 0 ${SPACING.s3}px;
    border-bottom: 1px solid ${COLORS.stroke};
  `,
  moduleTitle: css`
    label: FOCUSED_PROJECT_OVERVIEW_MODULE_TITLE;
    margin: 0;
    color: ${COLORS.ink};
    font-family: ${FONT.display};
    font-size: ${pxToRem(FONT_SIZE.bodyLarge)};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: 1.2;
  `,
  moduleActions: css`
    label: FOCUSED_PROJECT_OVERVIEW_MODULE_ACTIONS;
    display: flex;
    align-items: center;
    gap: ${SPACING.s2}px;
  `,
  moduleAction: css`
    label: FOCUSED_PROJECT_OVERVIEW_MODULE_ACTION;
    padding: 0;
    border: 0;
    border-bottom: 1px solid ${COLORS.stroke};
    background: transparent;
    color: ${COLORS.textSecondary};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;

    &:hover {
      border-bottom-color: ${COLORS.ink};
      color: ${COLORS.ink};
    }
  `,
  moduleToggle: css`
    label: FOCUSED_PROJECT_OVERVIEW_MODULE_TOGGLE;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${SPACING.s5}px;
    height: ${SPACING.s5}px;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${COLORS.textSecondary};
    cursor: pointer;

    &:hover {
      background: ${COLORS.sunken};
      color: ${COLORS.ink};
    }
  `,
  moduleEmpty: css`
    label: FOCUSED_PROJECT_OVERVIEW_MODULE_EMPTY;
    padding: ${SPACING.s3}px;
    color: ${COLORS.textSecondary};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  `,
  contractorBody: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_BODY;
    padding: ${SPACING.s3}px;
  `,
  contractorGeneral: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_GENERAL;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: ${SPACING.s1}px ${SPACING.s3}px;
    padding: ${SPACING.s2}px ${SPACING.s3}px;
    background: ${COLORS.sunken};
  `,
  contractorSummaryLabel: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_SUMMARY_LABEL;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
  contractorSummaryValue: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_SUMMARY_VALUE;
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    font-weight: ${FONT_WEIGHT.bold};
  `,
  contractorList: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_LIST;
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0 0 0 ${SPACING.s4}px;
    list-style: none;
    border-left: 1px solid ${COLORS.stroke};
  `,
  contractorOverrideList: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_OVERRIDE_LIST;
    display: flex;
    flex-direction: column;
    margin: ${SPACING.s2}px 0 0 ${SPACING.s3}px;
    padding: 0 0 0 ${SPACING.s3}px;
    border-left: 1px solid ${COLORS.stroke};
    list-style: none;
  `,
  contractorRow: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_ROW;
    display: flex;
    flex-direction: column;
    padding: ${SPACING.s2}px 0;
    border-bottom: 1px solid ${COLORS.stroke};

    &:last-child {
      border-bottom: 0;
    }
  `,
  contractorRowContent: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_ROW_CONTENT;
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr) auto;
    align-items: baseline;
    gap: ${SPACING.s1}px ${SPACING.s3}px;
    padding-left: ${SPACING.s3}px;

    &::before {
      position: absolute;
      top: 50%;
      left: calc(${SPACING.s3}px * -1);
      width: ${SPACING.s3}px;
      border-top: 1px solid ${COLORS.stroke};
      content: '';
    }

    @container focused-project-overview-root (max-width: 720px) {
      grid-template-columns: minmax(0, 1fr) auto;

      & > :nth-of-type(2) {
        grid-column: 1 / -1;
      }
    }
  `,
  contractorProvenance: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_PROVENANCE;
    min-width: 0;
    overflow: hidden;
    color: ${COLORS.textSecondary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.06em;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
  `,
  contractorName: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_NAME;
    min-width: 0;
    overflow: hidden;
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
    font-weight: ${FONT_WEIGHT.medium};
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  contractorUnassigned: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_UNASSIGNED;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: ${SPACING.s3}px;
    width: 100%;
    margin-top: ${SPACING.s3}px;
    padding: ${SPACING.s2}px ${SPACING.s3}px;
    border: 1px solid ${COLORS.error};
    background: ${COLORS.errorFill};
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    font-weight: ${FONT_WEIGHT.bold};
    text-align: left;
  `,
  contractorEditor: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_EDITOR;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${SPACING.s3}px;
    padding: ${SPACING.s3}px;

    @container focused-project-overview-root (max-width: 720px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @container focused-project-overview-root (max-width: 520px) {
      grid-template-columns: 1fr;
    }
  `,
  contractorTaskCount: css`
    label: FOCUSED_PROJECT_OVERVIEW_CONTRACTOR_TASK_COUNT;
    flex-shrink: 0;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-variant-numeric: tabular-nums;
  `,
  activityModule: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_MODULE;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.paper};
  `,
  activityTable: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_TABLE;
    overflow-x: auto;
  `,
  activityTableHeader: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_TABLE_HEADER;
    display: grid;
    grid-template-columns: calc(${SPACING.s8}px + ${SPACING.s3}px) ${SPACING.s3}px minmax(${SPACING.s6}px, 0.8fr) minmax(calc(${SPACING.s6}px * 2), 1.8fr) minmax(calc(${SPACING.s6}px * 2), 0.8fr) minmax(calc(${SPACING.s6}px * 2), 2fr) calc(${SPACING.s6}px * 5);
    gap: ${SPACING.s2}px;
    min-width: calc(${SPACING.s6}px * 16);
    padding: ${SPACING.s2}px ${SPACING.s3}px;
    background: ${COLORS.sunken};
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;

  `,
  activityAmountsHeader: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_AMOUNTS_HEADER;
    display: flex;
    justify-content: flex-end;
    gap: ${SPACING.s4}px;
    white-space: nowrap;
  `,
  activityPeriod: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_PERIOD;
    background: ${COLORS.paper};
  `,
  activityPeriodAlternate: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_PERIOD_ALTERNATE;
    background: ${COLORS.sunken};
  `,
  activityRow: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_ROW;
    display: grid;
    grid-template-columns: calc(${SPACING.s8}px + ${SPACING.s3}px) ${SPACING.s3}px minmax(${SPACING.s6}px, 0.8fr) minmax(calc(${SPACING.s6}px * 2), 1.8fr) minmax(calc(${SPACING.s6}px * 2), 0.8fr) minmax(calc(${SPACING.s6}px * 2), 2fr) calc(${SPACING.s6}px * 5);
    align-items: start;
    gap: ${SPACING.s2}px;
    min-width: calc(${SPACING.s6}px * 16);
    padding: ${SPACING.s3}px;
    border-bottom: 1px solid ${COLORS.stroke};
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodySmall)};
    line-height: 1.4;

  `,
  activityAmounts: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_AMOUNTS;
    display: flex;
    justify-content: flex-end;
    gap: ${SPACING.s4}px;
    min-width: 0;
  `,
  activityMarker: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_MARKER;
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    min-height: calc(${SPACING.s5}px + ${SPACING.s1}px);

    &::before {
      position: absolute;
      top: calc(${SPACING.s1}px * -3);
      bottom: calc(${SPACING.s1}px * -3);
      border-left: 1px solid ${COLORS.stroke};
      content: '';
    }
  `,
  activityMarkerDot: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_MARKER_DOT;
    z-index: 1;
    width: ${SPACING.s1}px;
    height: ${SPACING.s1}px;
    margin-top: calc(${SPACING.s1}px + ${SPACING.s1}px / 2);
    border: 1px solid ${COLORS.ink};
    background: ${COLORS.paper};
    transform: rotate(45deg);
  `,
  activityWhat: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_WHAT;
    display: flex;
    flex-wrap: wrap;
    gap: 0 ${SPACING.s1}px;
    min-width: 0;
    padding-right: ${SPACING.s4}px;
    color: ${COLORS.ink};
    font-weight: ${FONT_WEIGHT.medium};
  `,
  activityWhatButton: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_WHAT_BUTTON;
    display: flex;
    flex-wrap: wrap;
    gap: 0 ${SPACING.s1}px;
    min-width: 0;
    padding: 0 ${SPACING.s4}px 0 0;
    border: 0;
    background: transparent;
    color: ${COLORS.ink};
    cursor: pointer;
    font: inherit;
    font-weight: ${FONT_WEIGHT.medium};
    text-align: left;

    &:hover > :first-of-type {
      text-decoration: underline;
      text-underline-offset: ${SPACING.s1}px;
    }
  `,
  activityCaret: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_CARET;
    color: ${COLORS.textTertiary};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  `,
  activityRecipient: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_RECIPIENT;
    flex-basis: 100%;
    color: ${COLORS.textTertiary};
    font-size: ${pxToRem(FONT_SIZE.bodySmall)};
    font-weight: ${FONT_WEIGHT.regular};
  `,
  activityArtifact: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_ARTIFACT;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${SPACING.s1}px;
    min-width: 0;
  `,
  activityArtifactLabel: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_ARTIFACT_LABEL;
    color: ${COLORS.textSecondary};
    text-transform: capitalize;
  `,
  activityArtifactButton: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_ARTIFACT_BUTTON;
    width: fit-content;
    padding: 0;
    border: 0;
    border-bottom: 1px solid ${COLORS.ink};
    background: transparent;
    color: ${COLORS.ink};
    cursor: pointer;
    font: inherit;
    font-weight: ${FONT_WEIGHT.medium};
    text-align: left;
  `,
  activityArtifactStatus: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_ARTIFACT_STATUS;
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
  activityNote: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_NOTE;
    overflow: hidden;
    color: ${COLORS.textSecondary};
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  activityNoteButton: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_NOTE_BUTTON;
    padding: 0;
    border: 0;
    border-bottom: 1px solid ${COLORS.textSecondary};
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
    text-align: left;
  `,
  activityAmount: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_AMOUNT;
    color: ${COLORS.ink};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
  `,
  activityIncrease: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_INCREASE;
    color: ${COLORS.error};
  `,
  activityDecrease: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_DECREASE;
    color: ${COLORS.success};
  `,
  activityDetails: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_DETAILS;
    min-width: calc(${SPACING.s8}px * 12);
    padding: 0 ${SPACING.s3}px ${SPACING.s3}px calc(${SPACING.s8}px + ${SPACING.s3}px * 3 + ${SPACING.s2}px * 2);
    border-bottom: 1px solid ${COLORS.stroke};
    background: inherit;

    @container focused-project-overview-root (max-width: 959px) {
      min-width: 0;
      padding-left: ${SPACING.s3}px;
    }
  `,
  activityDetailGroup: css`
    display: flex;
    flex-direction: column;
    gap: ${SPACING.s2}px;
    padding: ${SPACING.s2}px 0;
    border-bottom: 1px solid ${COLORS.stroke};

    &:last-child {
      border-bottom: 0;
    }
  `,
  activityDetailGroupName: css`
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodySmall)};
    font-weight: ${FONT_WEIGHT.bold};
  `,
  activityDetailTaskList: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_DETAIL_TASK_LIST;
    display: flex;
    flex-direction: column;
    gap: ${SPACING.s2}px;
    margin: ${SPACING.s2}px 0 0;
    padding: 0;
    list-style: none;
  `,
  activityTaskChange: css`
    display: grid;
    grid-template-columns: calc(${SPACING.s8}px * 2) minmax(0, 1fr);
    align-items: baseline;
    gap: ${SPACING.s2}px;
  `,
  activityTaskId: css`
    color: ${COLORS.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
  activityTaskSummary: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_TASK_SUMMARY;
    display: flex;
    flex-wrap: wrap;
    gap: 0 ${SPACING.s2}px;
    min-width: 0;
  `,
  activityTaskName: css`
    color: ${COLORS.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodySmall)};
    font-weight: ${FONT_WEIGHT.medium};
  `,
  activityTaskDescription: css`
    color: ${COLORS.textTertiary};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  `,
  activityMore: css`
    label: FOCUSED_PROJECT_OVERVIEW_ACTIVITY_MORE;
    display: block;
    width: 100%;
    padding: ${SPACING.s2}px ${SPACING.s3}px;
    border: 0;
    background: transparent;
    color: ${COLORS.ink};
    cursor: pointer;
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodySmall)};
    font-weight: ${FONT_WEIGHT.bold};
    text-align: left;
    text-decoration: underline;
    text-underline-offset: ${SPACING.s1}px;

    &:hover {
      background: ${COLORS.sunken};
    }

    & + & {
      border-top: 1px solid ${COLORS.stroke};
    }
  `,
  fullWidthSection: css`
    label: FOCUSED_PROJECT_OVERVIEW_FULL_WIDTH_SECTION;
    min-width: 0;
  `,
};
